<template>
  <div class="product-feature-block" :class="{ 'product-feature-block_reverse': reverse }">
    <template v-if="reverse">
      <div class="product-feature-block_text" :style="textStyle">
        <h4>{{ heading }}</h4>
        <p>
          <template v-for="(line, index) in body" :key="index">
            {{ line }}<br v-if="index < body.length - 1" />
          </template>
        </p>
      </div>
      <div class="product-feature-block_media">
        <img :src="image" :alt="alt" />
      </div>
    </template>
    <template v-else>
      <div class="product-feature-block_media">
        <img :src="image" :alt="alt" />
      </div>
      <div class="product-feature-block_text" :style="textStyle">
        <h4>{{ heading }}</h4>
        <p>
          <template v-for="(line, index) in body" :key="index">
            {{ line }}<br v-if="index < body.length - 1" />
          </template>
        </p>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
/**
 * 支給デザインの `product-feature-block`（商品特徴のブロック）。**全38商品にあり1〜5個の可変**
 * （1個19商品・2個5・3個6・4個6・5個2。計81個。`_reverse` は27個）。
 *
 * **テキストの出どころは支給HTMLだけ**（決定#55）。既存の `commitment.vue` /
 * `commitmentMore.vue`（14商品・28ファイル）はタグと `<style>` を除いた実テキストが全ファイル0字で、
 * **こだわりの文章は画像に焼き込まれている**。したがって決定#46「移植元は既存コード」はここには
 * 適用できず、支給HTMLの `<h4>` と `<p>` を唯一の出どころとして商品別 const に起こす。
 * **既存コードに無い新規テキストなので、事業側の文言レビューが必要。**
 *
 * `_reverse` は画像とテキストを入れ替えるだけで、DOM の順番自体が変わる（CSS の
 * `flex-direction` ではなくマークアップで入れ替えている）ため、テンプレートを2通り持つ。
 *
 * 画像は商品別 const に**ファイル名**を持たせる（決定#59）。ただし複数商品で共有する画像は
 * `/image/product/common/` に置いて**フルパス**で渡す（決定#64）。
 * → CONVERSION-GUIDE.md §3.3.1 の #10
 */
type Props = {
  heading: string;
  /** 本文。1要素＝1行（決定#56） */
  body: string[];
  image: string;
  /** 省略時は `heading` を使う。支給の `alt` は使わない（下記） */
  imageAlt?: string;
  /** 画像を右、テキストを左にする（`product-feature-block_reverse`） */
  reverse?: boolean;
  /**
   * 本文（見出し＋本文）を隠す。**2026-08-20 の第8便で変わった方針**で、
   * 本文を画像に焼き込んだ商品が対象（→ `docs/design-renewal/PRODUCT-FEATURE.md`）。
   *
   * **支給と同じく `style="display: none;"` をインラインで出す。** 支給はこの状態に
   * クラスを与えていないので受け皿が無く、DOM から落とすと本文そのものが消える（決定#3）。
   */
  textHidden?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  imageAlt: '',
  reverse: false,
  textHidden: false,
});

/** 支給の `style="display: none;"` と同じもの。隠さないときは属性ごと出さない */
const textStyle = computed<string | undefined>(() =>
  props.textHidden ? 'display: none' : undefined
);

/**
 * **支給の `alt` は採用しない。** 81ブロックを全数照合すると
 * **`alt` が見出しと一致するのは3件だけで、51件が別ブロックからのコピペ・27件が空**
 * （例: `510` `512` `513` `516` `525` の `alt` がいずれも `501` の見出し
 * 「オリエンタルバイオがこだわりつくした3大成分」）。
 *
 * このブロックの画像は見出しと同じ主張を図にしたものなので、**見出しを代替テキストにする**のが
 * 内容として正しい。決定#51（支給の不備は差し戻さず実装側で吸収する）に従う。
 */
const alt = computed<string>(() => props.imageAlt || props.heading);
</script>
